import { Check, Copy } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'

type DetailFieldRowProps = {
  label: string
  value: ReactNode
  /**
   * Valor plano a copiar al portapapeles. Al pasarlo, la fila muestra un botón
   * de copiar al hover — para identificadores/correos que el admin necesita
   * llevarse (convención de filas especiales de los paneles de detalle).
   */
  copyValue?: string | null
  className?: string
}

/**
 * Fila etiqueta/valor de los paneles de detalle (familia compartida, antes
 * vivía en clients). Muestra guion cuando está vacía; `copyValue` la vuelve
 * copiable. Headless del dominio: solo presenta.
 */
export function DetailFieldRow({ label, value, copyValue, className }: DetailFieldRowProps) {
  const [copied, setCopied] = useState(false)
  const isEmpty = value == null || value === ''

  const handleCopy = async () => {
    if (!copyValue) return
    try {
      await navigator.clipboard.writeText(copyValue)
      setCopied(true)
      toast.success('Copiado al portapapeles', { duration: 1200 })
      setTimeout(() => {
        setCopied(false)
      }, 1500)
    } catch {
      toast.error('No se pudo copiar')
    }
  }

  return (
    <div className={cn('group flex flex-col gap-1 sm:flex-row sm:gap-2', className)}>
      <span className="text-sm text-muted-foreground sm:w-2/5">{label}</span>
      <div className="flex min-w-0 items-start gap-1 text-sm font-medium sm:w-3/5">
        <span className="min-w-0 break-words">{isEmpty ? '—' : value}</span>
        {copyValue && !isEmpty && (
          <button
            type="button"
            onClick={() => {
              void handleCopy()
            }}
            aria-label={`Copiar ${label.toLowerCase()}`}
            className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}
